import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { formatApiError } from '@/lib/utils';
import { CreditCard, Clock, CheckCircle2, AlertTriangle, Loader2, Users } from 'lucide-react';

const fmtF = (s) => { try { return new Date(s).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return s || '—'; } };

const PLANES = {
  independiente: { label: 'Abogado Independiente', precio: 'USD 9.99' },
  estudio_3:     { label: 'Estudio hasta 3',        precio: 'USD 24.99' },
  estudio_5:     { label: 'Estudio hasta 5',        precio: 'USD 44.99' },
  estudio_10:    { label: 'Estudio hasta 10',       precio: 'USD 79.99' },
  custom:        { label: 'A Medida',                precio: null        },
};

export default function Suscripcion() {
  const [estado, setEstado] = useState(null);
  const [uso, setUso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [err, setErr] = useState('');
  const [cotiz, setCotiz] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get('/api/suscripcion/estado'),
      api.get('/api/admin/plan/uso'),
      api.get('/api/suscripcion/mp/cotizacion'),
    ]).then(([est, u, c]) => {
      if (est.status === 'fulfilled') setEstado(est.value.data);
      if (u.status === 'fulfilled') setUso(u.value.data);
      if (c.status === 'fulfilled') setCotiz(c.value.data);
    }).finally(() => setLoading(false));
  }, []);

  const arsDe = (plan) => cotiz?.planes?.[plan]?.ars;
  const fmtArs = (n) => n != null ? '$' + Number(n).toLocaleString('es-AR') : null;
  // USD desde el backend (única fuente de verdad); si no cargó, usa el fallback.
  const usdDe = (plan) => cotiz?.planes?.[plan]?.usd;
  const fmtUsd = (plan, fallback) => { const u = usdDe(plan); return u != null ? `USD ${u}` : fallback; };

  const irACheckout = async () => {
    setCheckoutLoading(true); setErr('');
    try {
      const r = await api.post('/api/suscripcion/mp/checkout');
      if (r.data.checkout_url) window.open(r.data.checkout_url, '_blank');
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al iniciar el pago'));
    } finally { setCheckoutLoading(false); }
  };

  if (loading) {
    return <DashboardLayout title="Mi Suscripción"><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div></DashboardLayout>;
  }

  const plan = PLANES[estado?.plan] || { label: estado?.plan, precio: null };
  const enTrial = estado?.estado === 'trial';
  const activo = estado?.estado === 'activo';
  const suspendido = estado?.estado === 'suspendido' || estado?.estado === 'cancelado';
  const dias = estado?.dias_trial_restantes ?? 0;

  return (
    <DashboardLayout title="Mi Suscripción">
      <div className="max-w-2xl mx-auto space-y-5">

        {/* Estado actual */}
        <div className={`rounded-xl p-6 border ${
          activo ? 'bg-green-500/5 border-green-500/20'
          : enTrial && dias > 2 ? 'bg-[#C9A44C]/5 border-[#C9A44C]/20'
          : enTrial && dias <= 2 ? 'bg-orange-500/5 border-orange-500/25'
          : 'bg-red-500/5 border-red-500/20'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            {activo ? <CheckCircle2 className="w-6 h-6 text-green-400" />
              : enTrial ? <Clock className="w-6 h-6 text-[#C9A44C]" />
              : <AlertTriangle className="w-6 h-6 text-red-400" />}
            <div>
              <h2 className="text-base font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                {activo ? 'Suscripción activa'
                  : enTrial ? `Período de prueba${dias > 0 ? ` — ${dias} día${dias !== 1 ? 's' : ''} restante${dias !== 1 ? 's' : ''}` : ' — Vence hoy'}`
                  : 'Suscripción inactiva'}
              </h2>
              <p className="text-sm text-[#888]">{plan.label}</p>
            </div>
          </div>

          {enTrial && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-[#888] mb-1.5">
                <span>Días usados</span>
                <span>{5 - dias} / 5</span>
              </div>
              <div className="h-2 rounded-full bg-white/5">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${((5 - dias) / 5) * 100}%`, background: dias <= 1 ? 'linear-gradient(90deg, #EF4444, #F87171)' : 'linear-gradient(90deg, #C9A44C, #E0C878)' }} />
              </div>
            </div>
          )}

          {suspendido && (
            <div className="bg-red-500/10 rounded-lg p-3 mb-4">
              <p className="text-red-300 text-sm">Tu acceso está suspendido. Activá una suscripción para continuar usando LexOrbit.</p>
            </div>
          )}
        </div>

        {/* Plan y precio */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-6">
          <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-4">Tu plan</h3>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{plan.label}</span>
            {plan.precio && (
              <div className="text-right">
                <span className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>{fmtUsd(estado?.plan, plan.precio)}<span className="text-xs text-[#888] ml-1">/mes</span></span>
                {arsDe(estado?.plan) && (
                  <div className="text-[11px] text-[#888]">≈ {fmtArs(arsDe(estado?.plan))} ARS · dólar {cotiz?.dolar_tipo}</div>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-[#888]">
            {enTrial
              ? 'Estás en el período de prueba gratuito. Al finalizar podés activar la suscripción para seguir usando el sistema.'
              : activo
              ? 'Tu suscripción está activa. Se renueva automáticamente cada mes.'
              : 'Activá tu suscripción para acceder a todas las funcionalidades.'}
          </p>
        </div>

        {/* Uso del plan */}
        {uso && (
          <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-6">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-4">Uso del plan</h3>
            <div className="space-y-3">
              {/* Usuarios */}
              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#888]" />
                    <span>Usuarios</span>
                  </div>
                  <span className={`text-xs font-medium ${uso.puede_agregar ? 'text-green-400' : 'text-red-400'}`}>
                    {uso.usuarios_activos} / {uso.limite_usuarios >= 9999 ? '∞' : uso.limite_usuarios}
                  </span>
                </div>
                {uso.limite_usuarios < 9999 && (
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(100, (uso.usuarios_activos / uso.limite_usuarios) * 100)}%`,
                        background: uso.puede_agregar
                          ? 'linear-gradient(90deg, #C9A44C, #E0C878)'
                          : 'linear-gradient(90deg, #EF4444, #F87171)',
                      }} />
                  </div>
                )}
              </div>
            </div>

            {/* Tabla de planes */}
            <div className="mt-4 border-t border-white/5 pt-4">
              <p className="text-[11px] text-[#666] mb-3 uppercase tracking-wide">Comparativa de planes</p>
              <div className="space-y-1.5">
                {[
                  { plan: 'independiente', label: 'Independiente', usuarios: 1, precio: 'USD 9.99' },
                  { plan: 'estudio_3', label: 'Estudio hasta 3', usuarios: 3, precio: 'USD 24.99' },
                  { plan: 'estudio_5', label: 'Estudio hasta 5', usuarios: 5, precio: 'USD 44.99' },
                  { plan: 'estudio_10', label: 'Estudio hasta 10', usuarios: 10, precio: 'USD 79.99' },
                  { plan: 'custom', label: 'A medida', usuarios: '∞', precio: 'A convenir' },
                ].map((p) => (
                  <div key={p.plan}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all ${
                      uso.plan === p.plan
                        ? 'bg-[#C9A44C]/10 border border-[#C9A44C]/25'
                        : 'bg-white/[0.02] border border-transparent'
                    }`}>
                    <div className="flex items-center gap-2">
                      {uso.plan === p.plan && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#C9A44C]" />
                      )}
                      <span className={uso.plan === p.plan ? 'text-[#C9A44C] font-medium' : 'text-[#aaa]'}>
                        {p.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#888]">{p.usuarios} usuario{p.usuarios !== 1 && p.usuarios !== '∞' ? 's' : ''}</span>
                      <span className={uso.plan === p.plan ? 'text-[#C9A44C]' : 'text-[#666]'}>{p.plan === 'custom' ? p.precio : (fmtArs(arsDe(p.plan)) || fmtUsd(p.plan, p.precio))}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA de pago */}
        {!activo && (
          <div id="activar-suscripcion" className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-xl p-6 scroll-mt-24">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-3">Activar suscripción</h3>

            {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 mb-4 text-red-400 text-xs">{err}</div>}

            {estado?.mp_configurado ? (
              <>
                <p className="text-sm text-[#888] mb-4">
                  Al hacer clic serás redirigido a Mercado Pago. El precio es en USD y se cobra en pesos al
                  cambio del día{cotiz?.dolar_tipo ? ` (dólar ${cotiz.dolar_tipo})` : ''}. Suscripción mensual automática.
                </p>
                <button onClick={irACheckout} disabled={checkoutLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[#070707] font-semibold text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {checkoutLoading ? 'Preparando pago...'
                    : plan.precio
                      ? `Activar por ${fmtUsd(estado?.plan, plan.precio)}/mes${arsDe(estado?.plan) ? ` (≈ ${fmtArs(arsDe(estado?.plan))} ARS)` : ''}`
                      : 'Activar (precio a convenir)'}
                </button>
              </>
            ) : (
              <div className="text-center py-4">
                <CreditCard className="w-10 h-10 text-[#555] mx-auto mb-3" />
                <p className="text-sm text-[#888] mb-1">El sistema de pagos está en configuración.</p>
                <p className="text-xs text-[#666]">
                  Para activar tu suscripción contactá al administrador de LexOrbit.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info de facturación si está activo */}
        {activo && (
          <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-6">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-3">Facturación</h3>
            <p className="text-sm text-[#888]">
              Tu suscripción se gestiona a través de Mobbex. Para cancelar o modificar tu plan, contactá al soporte.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
